import { DirectUpload } from "@rails/activestorage";

document.addEventListener('DOMContentLoaded', function() {

  /* --------------------------------------------------
                PDF Page Count Function
  -------------------------------------------------- */
  const getPdfPageCount = (signedId) => {
    return fetch(`/api/v1/files/pdf_page_count?id=${encodeURIComponent(signedId)}`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        if (data.error) {
          throw new Error(data.error);
        }
        return data.page_count;
      });
  };

  /* --------------------------------------------------
                Active Storage Single File Upload
  -------------------------------------------------- */
  const vdActiveStorageUploadInit = function() {
    const inputQuery = '[data-js="active-storage-single-input"]';

    const uploadFile = (file, delegate) => {
      return new Promise((resolve, reject) => {
        const uploader = new DirectUpload(file, '/rails/active_storage/direct_uploads', delegate);
        uploader.create((error, blob) => {
          if (error) {
            reject(error);
          } else {
            resolve(blob);
          }
        });
      });
    };

    const handleFileUpload = async (e) => {
      const input = e.target;
      const file = input.files[0];
      if (!file) return;

      const container = input.closest('[data-js="active-storage-single-upload-container"]');
      if (!container) {
        console.error("Active Storage single upload container not found.");
        return;
      }

      const button = container.querySelector('[data-js="button"]');
      const linkContainer = container.querySelector('[data-js="link-container"]');
      const status = container.querySelector('[data-js="status"]');
      const hiddenInput = container.querySelector('[data-js="hidden-input"]');
      const filenameInput = container.querySelector('[data-js="filename-hidden-input"]');

      const delegate = {
        directUploadWillStoreFileWithXHR: (request) => {
          status.textContent = 'Uploading...';
          status.classList.remove('hidden');
          linkContainer.classList.add('hidden');

          request.upload.addEventListener("progress", event => {
            const progress = Math.round((event.loaded / event.total) * 100);
            status.textContent = `Uploading ${progress}%`;
          });
        }
      };

      try {
        const blob = await uploadFile(file, delegate);
        hiddenInput.value = blob.signed_id;
        
        // ** FIX: Set the filename on the new hidden input **
        if (filenameInput) {
          filenameInput.value = blob.filename;
        }

        status.textContent = 'Upload complete!';
        button.textContent = 'Replace';
        // ** FIX: Update the link text, but don't make it a link **
        linkContainer.innerHTML = `<span class="text-gray-900">${blob.filename}</span>`;
        
        // Get PDF page count if it's a PDF file
        if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
          try {
            const pageCount = await getPdfPageCount(blob.signed_id);
            const pillElement = container.querySelector('[data-js="pill"]');
            if (pillElement) {
              pillElement.textContent = pageCount === 1 ? `${pageCount} page` : `${pageCount} pages`;
            }
            const pdfPagesInput = container.querySelector(`input[data-js="pdf-pages"]`);
            if (pdfPagesInput) {
              pdfPagesInput.value = pageCount;
            }
          } catch (error) {
            console.error('Error getting PDF page count:', error);
          }
        }

        setTimeout(() => {
          status.classList.add('hidden');
          linkContainer.classList.remove('hidden');
        }, 2000);

      } catch (error) {
        status.textContent = 'Upload failed.';
        console.error(error);
        setTimeout(() => {
          status.classList.add('hidden');
          linkContainer.classList.remove('hidden');
        }, 2000);
      }
    };

    document.addEventListener('change', (e) => {
      if (e.target.matches(inputQuery)) {
        handleFileUpload(e);
      }
    });
  };

  /* --------------------------------------------------
                Active Storage Multiple File Upload
  -------------------------------------------------- */
  const vdActiveStorageUploadMultipleInit = function() {
    const inputQuery = '[data-js="active-storage-multiple-input"]';

    const uploadFile = (file, delegate) => {
      return new Promise((resolve, reject) => {
        // The direct upload URL is typically available at this path.
        const uploader = new DirectUpload(file, '/rails/active_storage/direct_uploads', delegate);
        uploader.create((error, blob) => {
          if (error) {
            reject(error);
          } else {
            resolve(blob);
          }
        });
      });
    };

    const handleUploads = async (e) => {
      const input = e.target;
      const files = Array.from(input.files);
      if (files.length === 0) return;

      const container = input.closest('[data-js="active-storage-multiple"]');
      if (!container) return;

      const trigger = container.querySelector('[data-js="active-storage-multiple-trigger"]');
      const progressContainer = container.querySelector('[data-js="active-storage-multiple-progress-container"]');
      const progressBar = container.querySelector('[data-js="active-storage-multiple-progress"]');
      const controls = container.querySelector('[data-js="active-storage-multiple-controls"]');
      const status = container.querySelector('[data-js="active-storage-multiple-controls-status"]');
      const cancel = container.querySelector('[data-js="active-storage-multiple-controls-cancel"]');
      const inputPrefix = input.dataset.prefix;

      let isCancelled = false;
      const cancelHandler = () => { isCancelled = true; };
      cancel.addEventListener('click', cancelHandler, { once: true });

      trigger.classList.add('hidden');
      progressContainer.classList.remove('hidden');
      controls.classList.remove('hidden');

      for (const [index, file] of files.entries()) {
        if (isCancelled) {
          status.textContent = 'Upload cancelled.';
          break;
        }

        const delegate = {
          directUploadWillStoreFileWithXHR: (request) => {
            request.upload.addEventListener("progress", event => {
              const progress = Math.round((event.loaded / event.total) * 100);
              progressBar.style.width = `${progress}%`;
              status.textContent = `Uploading ${file.name} (${progress}%)`;
            });
          }
        };

        try {
          status.textContent = `Preparing to upload ${file.name}...`;
          progressBar.style.width = '0%';
          const blob = await uploadFile(file, delegate);
          
          // Call the old function with an empty data object.
          // Its only job is to clone the template and give it a unique index.
          const newField = window.fieldAddDuplicatedNodeToPage(container, {});

          // Dispatch event for category detection
          const event = new CustomEvent('field-duplicatee:created', {
            bubbles: true,
            detail: { fieldElement: newField, filename: file.name }
          });
          container.dispatchEvent(event);

          // Now, we take control. Find the specific inputs inside the new element.
          const hiddenInput = newField.querySelector('[data-js="hidden-input"]');
          const linkContainer = newField.querySelector('[data-js="link-container"]');

          // Manually set the signed_id on the hidden input.
          if (hiddenInput) {
            hiddenInput.value = blob.signed_id;
          } else {
            console.error("MultipleUploader: Could not find hidden input in new field.");
          }

          // Manually update the display link.
          if (linkContainer) {
            const link = linkContainer.querySelector('a');
            if (link) {
              link.textContent = blob.filename;
              // We don't have a real URL yet, so we can't set href, but this updates the display.
            }
          }

          // Get PDF page count if it's a PDF file
          if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
            try {
              const pageCount = await getPdfPageCount(blob.signed_id);
              const pillElement = newField.querySelector('[data-js="pill"]');
              if (pillElement) {
                pillElement.textContent = pageCount === 1 ? `${pageCount} page` : `${pageCount} pages`;
              }
              const pdfPagesInput = newField.querySelector(`input[data-js="pdf-pages"]`);
              if (pdfPagesInput) {
                pdfPagesInput.value = pageCount;
              }
            } catch (error) {
              console.error('Error getting PDF page count:', error);
            }
          }

        } catch (error) {
          status.textContent = `Failed to upload ${file.name}.`;
          console.error(error);
          break;
        }
      }

      if (!isCancelled) {
        status.textContent = 'Upload complete.';
      }
      
      setTimeout(() => {
        trigger.classList.remove('hidden');
        progressContainer.classList.add('hidden');
        controls.classList.add('hidden');
        input.value = '';
      }, 2000);
      
      cancel.removeEventListener('click', cancelHandler);
    };

    document.addEventListener('change', (e) => {
      if (e.target.matches(inputQuery)) {
        handleUploads(e);
      }
    });
  };

  vdActiveStorageUploadInit();
  vdActiveStorageUploadMultipleInit();
});